import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ExtractedStepData } from "../app/pages/dashboards/_modals/create-app-stepper/IAppModels";

interface AnalyzeResponse {
  extractedData: ExtractedStepData;
  success: boolean;
}

interface ScoreResponse {
  overallScore: number;
  categoryScores: {
    marketOpportunity: number;
    customerUnderstanding: number;
    valueAndProduct: number;
    businessModel: number;
    validationExecution: number;
  };
  categoryReasons?: {
    marketOpportunity?: string;
    customerUnderstanding?: string;
    valueAndProduct?: string;
    businessModel?: string;
    validationExecution?: string;
  };
  redFlags: string[];
  strengths?: string[];
  recommendations: string[];
}

interface UseBusinessAIResult {
  analyzing: boolean;
  scoring: boolean;
  error: string | null;
  analyzeBusinessIdea: (
    businessDescription: string,
    projectName: string
  ) => Promise<ExtractedStepData | null>;
  calculateScore: (projectId: string) => Promise<ScoreResponse | null>;
  clearError: () => void;
}

/**
 * Hook for AI-powered business analysis and scoring
 */
export function useBusinessAI(): UseBusinessAIResult {
  const [analyzing, setAnalyzing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Analyze a business idea using Gemini AI
   * Returns extracted data for all 24 steps
   */
  const analyzeBusinessIdea = useCallback(
    async (
      businessDescription: string,
      projectName: string
    ): Promise<ExtractedStepData | null> => {
      if (!businessDescription.trim()) {
        setError("Please provide a business description");
        return null;
      }

      setAnalyzing(true);
      setError(null);

      try {
        const functions = getFunctions();
        const analyzeFunction = httpsCallable<
          { businessDescription: string; projectName: string },
          AnalyzeResponse
        >(functions, "analyzeBusinessIdea");

        const result = await analyzeFunction({
          businessDescription,
          projectName,
        });

        if (result.data.success && result.data.extractedData) {
          return result.data.extractedData;
        }

        throw new Error("Failed to extract business data");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to analyze business idea";
        setError(errorMessage);
        return null;
      } finally {
        setAnalyzing(false);
      }
    },
    []
  );

  /**
   * Calculate business strength score for a project
   */
  const calculateScore = useCallback(
    async (projectId: string): Promise<ScoreResponse | null> => {
      if (!projectId) {
        setError("Project ID is required");
        return null;
      }

      setScoring(true);
      setError(null);

      try {
        const functions = getFunctions();
        const scoreFunction = httpsCallable<{ projectId: string }, ScoreResponse>(
          functions,
          "calculateBusinessScore"
        );

        const result = await scoreFunction({ projectId });
        return result.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to calculate score";
        setError(errorMessage);
        return null;
      } finally {
        setScoring(false);
      }
    },
    []
  );

  return {
    analyzing,
    scoring,
    error,
    analyzeBusinessIdea,
    calculateScore,
    clearError,
  };
}
